import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import rough from "roughjs";

const energyLevels = { drained: 0, low: 1, medium: 2, high: 3 };

const EnergyTrendLineChart = ({ data, width = 320, height = 260, animate = true, lineColor = 'white' }) => {
    const svgRef = useRef();

    useEffect(() => {
        if (!data.length) return;

        const margin = { top: 20, right: 15, bottom: 30, left: 50 };
        const svg = d3.select(svgRef.current)
            .attr("viewBox", `0 0 400 250`)
            .attr("preserveAspectRatio", "xMidYMid meet");
            
        const innerWidth = 400 - margin.left - margin.right;
        const innerHeight = 250 - margin.top - margin.bottom;

        const minDate = new Date(data[0].date);
        const maxDate = new Date(data[data.length - 1].date);
        const xScale = d3
            .scaleTime()
            .domain([minDate, maxDate])
            .range([0, innerWidth]);

        const yScale = d3
            .scaleLinear()
            .domain([0, 3])
            .range([innerHeight, 0]);

        const line = d3
            .line()
            .x((d) => xScale(new Date(d.date)))
            .y((d) => yScale(energyLevels[d.energy]))
            .curve(d3.curveMonotoneX);

        const axesGroup = svg.append("g")
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        //#region Draw axes

        // Draw X-axis
        const roughSvg = rough.svg(svg.node()); // Attach Rough.js to the SVG
        const xAxisY = innerHeight;
        const xStart = 0;
        const xEnd = innerWidth;
        const strokeProps = { roughness: 1, stroke: "white", strokeWidth: 1 }
        const xAxis = roughSvg.line(xStart, xAxisY, xEnd, xAxisY, strokeProps);
        axesGroup.node().appendChild(xAxis);

        // Draw Y-axis
        const yStart = 0;
        const yEnd = innerHeight;
        const yAxis = roughSvg.line(xStart, yStart, xStart, yEnd, strokeProps);
        axesGroup.node().appendChild(yAxis);

        // Draw X-axis Labels
        const xTicks = xScale.ticks(d3.timeDay.every(1));
        xTicks.forEach((tick) => {
            const xPos = xScale(tick) + xStart + 8;

            axesGroup.append("text")
                .attr("x", xPos)
                .attr("y", xAxisY + 20)
                .attr("text-anchor", "middle")
                .style("font-size", "15px")
                .style("font-family", "Comic Sans MS, Comic Neue, Marker Felt, Chalkboard, sans-serif")
                .style("fill", "white")
                .text(d3.timeFormat("%a")(tick).toLowerCase());
        });

        // Draw Y-axis Labels
        const yTicks = yScale.ticks(4);
        yTicks.forEach((tick) => {
            const yPos = yScale(tick);

            axesGroup.append("text")
                .attr("x", xStart - 2)
                .attr("y", yPos + 4)
                .attr("text-anchor", "end")
                .style("font-size", "14px")
                .style("font-family", "Comic Sans MS, Comic Neue, Marker Felt, Chalkboard, sans-serif")
                .style("fill", "white")
                .text(Object.keys(energyLevels).find((key) => energyLevels[key] === tick));
        });
        //#endregion

        const pathGroup = svg.append("g")
            .attr("transform", `translate(${margin.left + 5},${margin.top})`);

        const path = pathGroup.append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", lineColor)
            .attr("stroke-width", 2)
            .attr("d", line);

        pathGroup.selectAll(".dot")
            .data(data)
            .enter()
            .append("circle")
            .attr("class", "dot")
            .attr("cx", (d) => xScale(d.date))
            .attr("cy", (d) => yScale(energyLevels[d.energy]))
            .attr("r", 2)
            .attr("fill", lineColor);

        if (animate) {
            const totalLength = path.node().getTotalLength();
            path.attr("stroke-dasharray", totalLength + " " + totalLength)
                .attr("stroke-dashoffset", totalLength)
                .transition()
                .duration(3000)
                .ease(d3.easeLinear)
                .attr("stroke-dashoffset", 0);
        }

    }, [data, width, height]);

    return (
        <div style={{ width: "100%", height: "auto", display: "flex", justifyContent: "center" }}>
            <svg ref={svgRef} width="100%"/>
        </div>
    )
};

export default EnergyTrendLineChart;
